<template>
  <div class="pages-dev-tenancy-company-selector-test">
    <h3>Tenancy Company Name Selector Test</h3>
    <p class="hint">
      Compare legacy style and current restored style side-by-side.
    </p>

    <div class="compare-grid">
      <div class="company-row">
        <h4>Current HKLSelect</h4>
        <div class="search__company search__company--name is-current">
          <HKLSelect
            v-hkldinput
            class="seacrh__company-selector"
            :value="currentCompanyNameValue"
            :options="companyNameHklOptions"
            :showSearch="true"
            :showArrow="false"
            :singleTaggableInput="true"
            placeholder=""
            @search="onCurrentCompanyNameSearch"
            @input="onCurrentCompanyNameInput"
            @change="onCurrentCompanyNameChange"
          />
          <span
            v-show="!currentCompanyNameValue"
            class="fa fa-search"
          />
        </div>
      </div>

      <div class="company-row">
        <h4>Old version (e8becf9)</h4>
        <div class="search__company search__company--name is-old">
          <MultiSelect
            v-hkldinput
            class="seacrh__company-selector"
            :required="true"
            placeholder=""
            selectLabel=""
            selectedLabel=""
            deselectLabel=""
            :searchable="true"
            :taggable="true"
            :commitTagOnBlur="true"
            tagPlaceholder="Add this as temporary company"
            :value="legacyCompanyNameValue"
            :options="companyNameOptions"
            @tag="onLegacyCompanyNameChange"
            @search-change="onLegacyCompanyNameSearch"
            @select="onLegacyCompanyNameChange"
          />
          <span
            v-show="!legacyCompanyNameValue"
            class="fa fa-search"
          />
        </div>
      </div>
    </div>

    <div class="company-row">
      <h4>Current Trade Name (openOnFocus)</h4>
      <HKLSelect
        v-hkldinput
        class="seacrh__company-selector"
        :value="currentTradeNameValue"
        :options="tradeNameHklOptions"
        :showSearch="true"
        :showArrow="false"
        :singleTaggableInput="true"
        :openOnFocus="true"
        placeholder=""
        @input="onCurrentTradeNameInput"
        @change="onCurrentTradeNameChange"
      />
    </div>

    <pre class="state-debug">{{ JSON.stringify({ currentCompanyNameValue, legacyCompanyNameValue, currentTradeNameValue }, null, 2) }}</pre>
  </div>
</template>

<script>
import HKLSelect from '@/controls/Select/HKLSelect'
import MultiSelect from '@/controls/MultiSelect'

const companyNameSamples = [
  'Acme Properties Ltd (A1001)',
  'Beta Trading Co (B2001)',
  'City Logistics Group (C3001)',
  'Delta Construction (D4001)',
  'Evergreen Commerce (E5001)',
]
const tradeNameSamples = [
  'Acme Retail',
  'Acme Office',
  'Beta Counter',
]

export default {
  name: 'TenancyCompanySelectorTest',
  components: {
    HKLSelect,
    MultiSelect,
  },
  data () {
    return {
      allCompanyNames: companyNameSamples,
      currentCompanyNameValue: 'Acme Properties Ltd (A1001)',
      currentCompanyNameSearchText: '',
      legacyCompanyNameValue: 'Acme Properties Ltd (A1001)',
      currentTradeNameValue: 'Acme Retail',
    }
  },
  computed: {
    companyNameOptions () {
      return this.allCompanyNames || []
    },
    companyNameHklOptions () {
      const options = (this.allCompanyNames || []).map(name => ({
        name,
        value: name,
      }))
      const temporaryCompanyNameOption = this.getTemporaryCompanyNameOption()
      return temporaryCompanyNameOption ? [temporaryCompanyNameOption, ...options] : options
    },
    tradeNameHklOptions () {
      return tradeNameSamples.map(name => ({ name, value: name }))
    },
  },
  methods: {
    getTemporaryCompanyNameOption () {
      const searchText = String(this.currentCompanyNameSearchText || '').trim()
      if (!searchText) return null
      const normalizedSearch = searchText.toLowerCase()
      const exists = (this.allCompanyNames || []).some((name) =>
        String(name).trim().toLowerCase() === normalizedSearch,
      )
      if (exists) return null
      return {
        name: searchText,
        value: searchText,
        isSingleTaggableInputTag: true,
        hklSelectHint: 'Add this as temporary company',
      }
    },
    onCurrentCompanyNameSearch (query) {
      this.currentCompanyNameSearchText = query || ''
      const lowerQuery = String(query || '').trim().toLowerCase()
      if (!lowerQuery) {
        this.allCompanyNames = companyNameSamples
        return
      }
      this.allCompanyNames = companyNameSamples.filter((name) =>
        name.toLowerCase().includes(lowerQuery),
      )
    },
    onCurrentCompanyNameChange (val) {
      const value = val ? String(val) : ''
      if (!value) {
        this.currentCompanyNameSearchText = ''
        this.currentCompanyNameValue = ''
        return
      }
      this.currentCompanyNameSearchText = ''
      this.currentCompanyNameValue = value
      if (!this.allCompanyNames.includes(value)) {
        this.allCompanyNames = [value, ...this.allCompanyNames]
      }
    },
    onCurrentCompanyNameInput (val) {
      this.currentCompanyNameValue = val ? String(val) : ''
    },
    onLegacyCompanyNameSearch (query) {
      const lowerQuery = String(query || '').trim().toLowerCase()
      if (!lowerQuery) {
        this.allCompanyNames = companyNameSamples
        return
      }
      this.allCompanyNames = companyNameSamples.filter((name) =>
        name.toLowerCase().includes(lowerQuery),
      )
    },
    onLegacyCompanyNameChange (val) {
      const value = val ? String(val) : ''
      if (!value) {
        this.legacyCompanyNameValue = ''
        return
      }
      this.legacyCompanyNameValue = value
      if (!this.allCompanyNames.includes(value)) {
        this.allCompanyNames = [value, ...this.allCompanyNames]
      }
    },
    onCurrentTradeNameChange (val) {
      this.currentTradeNameValue = val ? String(val) : ''
    },
    onCurrentTradeNameInput (val) {
      this.currentTradeNameValue = val ? String(val) : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.pages-dev-tenancy-company-selector-test {
  max-width: 760px;
  margin: 24px auto;
  padding: 0 16px;

  .hint {
    margin-bottom: 12px;
    color: #4a4a4a;
  }
}

.compare-grid {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.state-debug {
  width: 100%;
  max-width: 520px;
  background: #f5f7fa;
  border: 1px solid #d6dbe3;
  padding: 10px;
  margin-top: 18px;
}

.company-row {
  width: 100%;
  max-width: 360px;

  & h4 {
    margin: 0 0 8px;
    color: #0c3a58;
    font-size: 14px;
    font-weight: 600;
  }

  .search__company {
    position: relative;

    .seacrh__company-selector,
    .hkl-select {
      width: 100%;
    }

    .fa-search {
      position: absolute;
      top: 8px;
      left: 11px;
      font-size: 23px;
      color: #B5B5B5;
      opacity: 1;
    }

    .hkl-select:focus-within + .fa-search,
    .hkl-select.ant-select-open + .fa-search,
    .hkl-select.ant-select-focused + .fa-search {
      display: none;
    }
  }

  .is-current {
    .seacrh__company-selector {
      ::v-deep .ant-select-selection__choice {
        background: transparent !important;
        color: #35495E !important;
        text-transform: none !important;
        font-weight: normal !important;
        margin: 0 !important;
        padding: 0 4px !important;
        border: none !important;
      }

      ::v-deep .ant-select-selection__choice__remove {
        display: none !important;
      }
    }
  }
}
</style>
